import { reactive } from 'vue'

interface AuthState {
  isAuthenticated: boolean
  userName: string
  userEmail: string
  userHandle: string
  isAdmin: boolean
  role: string
  loading: boolean
  error: string | null
  lastFetched: number | null
}

// Cache duration in milliseconds (10 minutes)
const CACHE_DURATION = 10 * 60 * 1000

const state = reactive<AuthState>({
  isAuthenticated: false,
  userName: '',
  userEmail: '',
  userHandle: '',
  isAdmin: false,
  role: 'Member',
  loading: false,
  error: null,
  lastFetched: null
})

export function useAuthStore() {
  const isStale = () => {
    if (!state.lastFetched) return true
    return Date.now() - state.lastFetched > CACHE_DURATION
  }

  const checkAuth = async (force = false) => {
    // Don't fetch if we have recent data and not forcing refresh
    if (!force && !isStale() && !state.loading) {
      return {
        isAuthenticated: state.isAuthenticated,
        userName: state.userName,
        userEmail: state.userEmail,
        userHandle: state.userHandle,
        isAdmin: state.isAdmin,
        role: state.role
      }
    }

    // Don't fetch if already loading
    if (state.loading) {
      return {
        isAuthenticated: state.isAuthenticated,
        userName: state.userName,
        userEmail: state.userEmail,
        userHandle: state.userHandle,
        isAdmin: state.isAdmin,
        role: state.role
      }
    }

    state.loading = true
    state.error = null

    try {
      // Check authentication
      const authResponse = await fetch(`${process.env.VUE_APP_API_BASE_URL}/check-auth`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!authResponse.ok) {
        throw new Error('Failed to check authentication')
      }

      const authData = await authResponse.json()
      
      if (!authData.isAuthenticated) {
        state.isAuthenticated = false
        state.userName = ''
        state.userEmail = ''
        state.userHandle = ''
        state.isAdmin = false
        state.role = 'Member'
        state.lastFetched = Date.now()
        return {
          isAuthenticated: false,
          userName: '',
          userEmail: '',
          userHandle: '',
          isAdmin: false,
          role: 'Member'
        }
      }

      // Update basic auth info
      state.isAuthenticated = true
      state.userName = authData.name || ''
      state.userEmail = authData.email || ''
      state.userHandle = authData.handle || authData.email || '' // Backend now sends handle, fallback to email

      // Check user role with retry logic
      console.log('🔐 Auth store: Fetching user role...')
      let roleData = null
      let retryCount = 0
      const maxRetries = 3
      
      while (retryCount < maxRetries && !roleData) {
        try {
          const roleResponse = await fetch(`${process.env.VUE_APP_API_BASE_URL}/user/role`, {
            credentials: 'include'
          })

          console.log(`🔐 Auth store: Role response status (attempt ${retryCount + 1}):`, roleResponse.status)

          if (roleResponse.ok) {
            roleData = await roleResponse.json()
            console.log('🔐 Auth store: Role data received:', roleData)
            break
          } else {
            console.log(`🔐 Auth store: Role check failed (attempt ${retryCount + 1}), retrying...`)
            retryCount++
            if (retryCount < maxRetries) {
              // Wait a bit before retrying
              await new Promise(resolve => setTimeout(resolve, 1000 * retryCount))
            }
          }
        } catch (error) {
          console.log(`🔐 Auth store: Role fetch error (attempt ${retryCount + 1}):`, error)
          retryCount++
          if (retryCount < maxRetries) {
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount))
          }
        }
      }

      if (roleData) {
        state.role = roleData.role || 'Member'
        state.isAdmin = roleData.role === 'Admin'
        console.log('🔐 Auth store: Set role to:', state.role)
        console.log('🔐 Auth store: Set isAdmin to:', state.isAdmin)
      } else {
        // Fallback to member role if all retries failed
        console.log('🔐 Auth store: All role check attempts failed, using fallback')
        state.role = 'Member'
        state.isAdmin = false
      }

      state.lastFetched = Date.now()
      
      return {
        isAuthenticated: state.isAuthenticated,
        userName: state.userName,
        userEmail: state.userEmail,
        userHandle: state.userHandle,
        isAdmin: state.isAdmin,
        role: state.role
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Failed to check authentication'
      console.error('Error checking authentication:', error)
      throw error
    } finally {
      state.loading = false
    }
  }

  const refreshAuth = () => {
    return checkAuth(true)
  }

  const clearAuth = () => {
    state.isAuthenticated = false
    state.userName = ''
    state.userEmail = ''
    state.userHandle = ''
    state.isAdmin = false
    state.role = 'Member'
    state.lastFetched = null
    state.error = null
  }

  const getAuthState = () => {
    return {
      isAuthenticated: state.isAuthenticated,
      userName: state.userName,
      userEmail: state.userEmail,
      userHandle: state.userHandle,
      isAdmin: state.isAdmin,
      role: state.role,
      loading: state.loading,
      error: state.error
    }
  }

  return {
    checkAuth,
    refreshAuth,
    clearAuth,
    getAuthState
  }
} 